import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, empresaAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [empresa, setEmpresa] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadEmpresa = useCallback(async (empresaId) => {
        try {
            let empresaData;
            if (empresaId) {
                const res = await empresaAPI.get(empresaId);
                empresaData = res.data;
            } else {
                // Superadmin sin empresa: cargar la primera disponible
                const res = await empresaAPI.getAll();
                const list = Array.isArray(res.data) ? res.data : [];
                if (list.length > 0) {
                    empresaData = list[0];
                }
            }
            if (empresaData) {
                setEmpresa(empresaData);
                localStorage.setItem('empresa', JSON.stringify(empresaData));
                return empresaData;
            }
            setEmpresa(null);
            return null;
        } catch (err) {
            console.error('Error loading empresa:', err);
            setEmpresa(null);
            return null;
        }
    }, []);

    const loadUser = useCallback(async () => {
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');
        
        if (token && savedUser) {
            try {
                const response = await authAPI.me();
                setUser(response.data);
                localStorage.setItem('user', JSON.stringify(response.data));
                // Load empresa
                await loadEmpresa(response.data.empresa_id);
            } catch (err) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('empresa');
                setUser(null);
                setEmpresa(null);
            }
        }
        setLoading(false);
    }, [loadEmpresa]);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    const login = async (email, password) => {
        setError(null);
        try {
            const response = await authAPI.login({ email, password });
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('token', access_token);
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            
            // Load empresa
            await loadEmpresa(userData.empresa_id);
            
            return userData;
        } catch (err) {
            const message = err.response?.data?.detail || 'Error al iniciar sesión';
            setError(message);
            throw new Error(message);
        }
    };

    const register = async (userData) => {
        setError(null);
        try {
            const response = await authAPI.register(userData);
            const { access_token, user: newUser } = response.data;
            
            localStorage.setItem('token', access_token);
            localStorage.setItem('user', JSON.stringify(newUser));
            setUser(newUser);
            
            if (newUser.empresa_id) {
                await loadEmpresa(newUser.empresa_id);
            }
            
            return newUser;
        } catch (err) {
            const message = err.response?.data?.detail || 'Error al registrarse';
            setError(message);
            throw new Error(message);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('empresa');
        localStorage.removeItem('pi-theme-mode');
        setUser(null);
        setEmpresa(null);
        // Reset page title
        document.title = 'Protección Industrial';
    };

    const hasRole = (...roles) => {
        return user && roles.includes(user.rol);
    };

    const refreshUser = useCallback(async () => {
        try {
            const response = await authAPI.me();
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            if (response.data.empresa_id) {
                await loadEmpresa(response.data.empresa_id);
            }
            return response.data;
        } catch { return null; }
    }, [loadEmpresa]);

    const switchEmpresa = useCallback(async (empresaId) => {
        await loadEmpresa(empresaId);
    }, [loadEmpresa]);

    const value = {
        user,
        empresa,
        loading,
        error,
        login,
        register,
        logout,
        hasRole,
        refreshUser,
        loadEmpresa,
        switchEmpresa,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
